import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CustomOption, CustomSingleValue } from '@/components';
import { getGlobalSelectClass } from '@/utils';

const SelectInput = ({
  label,
  value,
  onChange,
  type,
  fetchLookup,
  loadOptions,
  pageSize = 20,
  debounceMs = 300,
  staticOptions = [],
  isMulti = false,
  placeholder = 'Select...',
  customClass,
  isClearable = true,
  components,
  /** NEW: params that affect the lookup (e.g., { contactId }) */
  lookupParams = null,
  /** Optional: force reload when this key changes (string/number) */
  refreshKey,
  /** Optional: fetch immediately if desired (default: fetch on open) */
  fetchOnMount = false,
  onOptionSelect,
  onInputChange
}) => {
  const isAsync = typeof loadOptions === 'function';
  const [options, setOptions] = useState(staticOptions || []);
  const [hasFetched, setHasFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const [asyncPage, setAsyncPage] = useState(1);
  const [asyncHasMore, setAsyncHasMore] = useState(true);
  const latestSearchRef = useRef('');
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const [inputValue, setInputValue] = useState('');

  // Compute a stable signature of lookup params to detect changes
  const paramsSignature = useMemo(() => {
    try { return JSON.stringify(lookupParams ?? null); }
    catch { return ''; }
  }, [lookupParams]);

  // Invalidate cache when params or refreshKey change
  useEffect(() => {
    if (staticOptions?.length > 0) return;
    setHasFetched(false);
    setOptions([]);
    if (isAsync) {
      setAsyncOptions([]);
      setAsyncPage(1);
      setAsyncHasMore(true);
      latestSearchRef.current = '';
    }
  }, [paramsSignature, String(refreshKey ?? '')]);

  const doFetch = useCallback(async () => {
    if (!type || !fetchLookup) return;

    setLoading(true);
    try {
      const fetched = await fetchLookup(type, lookupParams || {});
      setOptions(Array.isArray(fetched) ? fetched : []);
      setHasFetched(true);
    } catch (err) {
      console.error('Lookup fetch error:', err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [type, fetchLookup, lookupParams]);

  const mergeOptions = (prev, incoming) => {
    const map = new Map();
    const keyOf = (opt) => String(opt?.value ?? opt?.id ?? opt?.label ?? '');
    prev.forEach((opt) => {
      const key = keyOf(opt);
      if (key) map.set(key, opt);
    });
    incoming.forEach((opt) => {
      const key = keyOf(opt);
      if (key) map.set(key, opt);
    });
    return Array.from(map.values());
  };

  const loadAsync = async ({ search, page, replace }) => {
    const requestId = ++requestIdRef.current;
    setAsyncLoading(true);
    try {
      const result = await loadOptions({ search, page, pageSize, ...(lookupParams || {}) });
      if (requestId !== requestIdRef.current) return;

      const nextOptions = result?.options || [];
      setAsyncHasMore(Boolean(result?.hasMore));
      setAsyncPage(page);
      setAsyncOptions(prev => (replace ? nextOptions : mergeOptions(prev, nextOptions)));
    } finally {
      if (requestId === requestIdRef.current) setAsyncLoading(false);
    }
  };

  const handleMenuOpen = async () => {
    if (isAsync) {
      if (!asyncOptions.length) {
        await loadAsync({ search: latestSearchRef.current || '', page: 1, replace: true });
      }
      return;
    }
    if (staticOptions?.length > 0) return;
    if (hasFetched) return;                 // respect cache until params change
    await doFetch();
  };

  const handleInput = (inputValue, actionMeta) => {
    if (onInputChange) onInputChange(inputValue, actionMeta);
    if (actionMeta?.action === 'input-change') {
      setInputValue(inputValue);
    }
    if (!isAsync || actionMeta?.action !== 'input-change') return inputValue;

    latestSearchRef.current = inputValue;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setAsyncHasMore(true);
      loadAsync({ search: inputValue, page: 1, replace: true });
    }, debounceMs);

    return inputValue;
  };

  const handleMenuClose = () => {
    latestSearchRef.current = '';
    setInputValue('');
    if (!isAsync) return;
    setAsyncHasMore(true);
    setAsyncPage(1);
    setAsyncOptions([]);
  };

  const handleMenuScrollToBottom = () => {
    if (!isAsync || asyncLoading || !asyncHasMore) return;
    loadAsync({ search: latestSearchRef.current || '', page: asyncPage + 1, replace: false });
  };

  // Optional: fetch on mount (and on param/refresh changes)
  useEffect(() => {
    if (!fetchOnMount) return;
    if (staticOptions?.length > 0) return;
    if (hasFetched) return;
    if (isAsync) return;

    doFetch();
  }, [fetchOnMount, paramsSignature, refreshKey, staticOptions?.length, hasFetched, doFetch, isAsync]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const computedOptions = isAsync ? asyncOptions : options;
  const computedLoading = isAsync ? asyncLoading : loading;

  return (
    <div className={`w-1/3 ${customClass}`}>
      {label && <label className='mb-1 w-fit form-label'>{label}</label>}
      <Popover>
        <PopoverTrigger asChild>
          <button className="w-full py-3 leading-4 btn btn-sm btn-light h-fit text-start">
            {Array.isArray(value) && value.length
              ? value.map(item => item?.label || '').join(', ')
              : value?.label ?? label}
          </button>
        </PopoverTrigger>
        <PopoverContent
          className="w-full p-0"
          align="end"
          style={{ width: 'var(--radix-popover-trigger-width)' }}
        >
          <div className='w-full'>
            <Select
              classNames={getGlobalSelectClass()}
              classNamePrefix="filter-select-input capitalize"
              className='w-full'
              isMulti={isMulti}
              value={value}
              onChange={(selectedOption) => {
                onChange(selectedOption);
                if (onOptionSelect) {
                  onOptionSelect(selectedOption); // ✅ call your custom handler
                }
              }}
              onMenuOpen={handleMenuOpen}
              onMenuClose={handleMenuClose}
              onBlur={handleMenuClose}
              onInputChange={handleInput}
              onMenuScrollToBottom={handleMenuScrollToBottom}
              options={computedOptions}
              isLoading={computedLoading}
              placeholder={placeholder}
              isClearable={isClearable}
              inputValue={inputValue}
              filterOption={isAsync ? () => true : undefined}
              components={
                components ||
                (type === 'users' || type === 'contact'
                  ? { Option: CustomOption, SingleValue: CustomSingleValue }
                  : undefined)
              }
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export { SelectInput };
